// Derivations for the unattended PinGolf club screen (pages/PinGolfLeagueTv). Everything here
// is computed from cards that have already been submitted - the board asks for no data entry
// of its own, so a week that was played is a week the strip can talk about.

import { formatVsPar } from './pingolfScoring';
import { formatScoreAbbreviated } from './formatScore';
import { personName } from './personName';

// One naming rule for every board, so the TV and the app agree.
export const playerName = personName;

// Hole number -> machine name, read off the boards rather than the lineup: the week lineup
// stores machine IDs only, and the leaderboard is the one place the names are already joined.
export function machineNamesByHole(netBoard) {
  const names = new Map();
  for (const entry of netBoard || []) {
    for (const hole of entry.holes || []) {
      if (hole.machine_name) names.set(Number(hole.hole_number), hole.machine_name);
    }
  }
  return names;
}

// The night's course, as the strip reads it: "3 · Jacks Open · 60.0K · 5 balls". Targets and
// ball counts come from the session course actually cut for the night (not the week lineup,
// which a host can have deviated from); names come from the board. A hole nobody has played
// yet has no name to show, so it waits rather than scrolling past as "Hole 4".
export function buildCourseStrip(week, netBoard) {
  const session = ((week && week.sessions) || [])[0];
  const names = machineNamesByHole(netBoard);

  return ((session && session.holes) || [])
    .map(hole => ({
      holeNumber: Number(hole.holeNumber),
      machineName: names.get(Number(hole.holeNumber)) || null,
      targetScore: hole.targetScore,
      maxBalls: hole.maxBalls
    }))
    .filter(hole => hole.machineName)
    .sort((a, b) => a.holeNumber - b.holeNumber)
    .map(hole => ({
      lead: String(hole.holeNumber),
      main: hole.machineName,
      sub: [
        hole.targetScore ? formatScoreAbbreviated(hole.targetScore) : null,
        hole.maxBalls ? `${hole.maxBalls} balls` : null
      ].filter(Boolean).join(' · ')
    }));
}

// Every hole actually shot this week, best first.
function recordedHoles(netBoard) {
  const holes = [];
  for (const entry of netBoard || []) {
    for (const hole of entry.holes || []) {
      if (hole.unplayed || hole.hole_result === null || hole.hole_result === undefined) continue;
      holes.push({
        result: Number(hole.hole_result),
        holeNumber: Number(hole.hole_number),
        machineName: hole.machine_name || `Hole ${hole.hole_number}`,
        player: playerName(entry)
      });
    }
  }
  return holes.sort((a, b) => a.result - b.result || a.holeNumber - b.holeNumber);
}

// The week's standouts. Each is one chip plus a line of context; a stat the week cannot
// support (nobody has finished a card, the field went under every hole) is left out rather
// than rendered empty, so an early-evening strip is short instead of full of placeholders.
export function buildWeekHighlights(netBoard, weekNumber) {
  const entries = netBoard || [];
  const holes = recordedHoles(entries);
  if (holes.length === 0) return [];

  const highlights = [];

  const best = holes[0];
  highlights.push({
    chip: formatVsPar(best.result),
    tone: best.result < 0 ? 'good' : 'note',
    main: `${best.player} on ${best.machineName}`,
    sub: 'Best hole of the week'
  });

  // Second best only when it is a different player - one card's two good holes read as a
  // repeat on a strip that is already short.
  const runnerUp = holes.slice(1).find(hole => hole.player !== best.player);
  if (runnerUp) {
    highlights.push({
      chip: formatVsPar(runnerUp.result),
      tone: runnerUp.result < 0 ? 'good' : 'note',
      main: `${runnerUp.player} on ${runnerUp.machineName}`,
      sub: 'Second best hole'
    });
  }

  const underPar = entries
    .map(entry => ({
      player: playerName(entry),
      count: (entry.holes || []).filter(hole => !hole.unplayed && hole.hole_result < 0).length
    }))
    .sort((a, b) => b.count - a.count)[0];
  if (underPar && underPar.count > 0) {
    highlights.push({
      chip: String(underPar.count),
      tone: 'note',
      main: `${underPar.player} · holes under par`,
      sub: 'Most holes under on one card'
    });
  }

  // Raw card, not net: the handicap is the standings' business, and the lowest card of the
  // night is the thing the room actually watched happen. Unfinished cards are excluded -
  // a running total is not a card yet.
  const lowestCard = entries
    .filter(entry => entry.is_complete && entry.total_score !== null && entry.total_score !== undefined)
    .sort((a, b) => a.total_score - b.total_score)[0];
  if (lowestCard) {
    highlights.push({
      chip: formatVsPar(lowestCard.total_score),
      tone: lowestCard.total_score < 0 ? 'good' : 'note',
      main: playerName(lowestCard),
      sub: weekNumber ? `Lowest raw card, Week ${weekNumber}` : 'Lowest raw card'
    });
  }

  // The hole that held: among the holes nobody got under par on, the one whose best result
  // was worst. Silent when the field went under everything, which is the honest outcome.
  const bestByHole = new Map();
  for (const hole of holes) {
    const current = bestByHole.get(hole.holeNumber);
    if (!current || hole.result < current.result) bestByHole.set(hole.holeNumber, hole);
  }
  const hardest = Array.from(bestByHole.values())
    .filter(hole => hole.result >= 0)
    .sort((a, b) => b.result - a.result)[0];
  if (hardest) {
    highlights.push({
      chip: formatVsPar(hardest.result),
      tone: 'note',
      main: hardest.machineName,
      sub: 'Hardest hole · nobody went under'
    });
  }

  return highlights;
}

// Lays the standouts through the course rather than after it, so a viewer who catches the
// strip mid-loop sees one within a few seconds instead of sitting through nine machine names.
export function interleaveTicker(course, highlights) {
  if (course.length === 0) return highlights;
  if (highlights.length === 0) return course;

  const items = [];
  const gap = Math.max(1, Math.round(course.length / highlights.length));
  let next = 0;

  course.forEach((hole, index) => {
    items.push(hole);
    if ((index + 1) % gap === 0 && next < highlights.length) {
      items.push(highlights[next]);
      next += 1;
    }
  });

  return items.concat(highlights.slice(next));
}

// Every page of the standings is full: the last page backs up over the previous one rather
// than padding with blank rows, which on signage reads as the field having ended early.
export function pageSlice(rows, perPage, tick) {
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const page = pages <= 1 ? 0 : tick % pages;
  const start = Math.max(0, Math.min(page * perPage, total - perPage));
  const slice = rows.slice(start, start + perPage);
  return { slice, page, pages, start, total, end: start + slice.length };
}
